import React, { Fragment, useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Row, Col, Card, Modal, Button, Form, Spinner } from "react-bootstrap";
import useRequests from "../../../utils/useRequests";
import * as Yup from "yup";
import { useFormik } from "formik";
import Snackbars from "../../../utils/Snackbars";
import DataTable from "react-data-table-component";

const SeatCategories = () => {
    const { v_id } = useParams();
    const [venue, setVenue] = useState({});
    const [seatCategories, setSeatCategories] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [modalType, setModalType] = useState("add");
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [loading, setLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [statusProcessing, setStatusProcessing] = useState(false);
    const [deleteProcessing, setDeleteProcessing] = useState(false);
    const [submitProcessing, setSubmitProcessing] = useState(false);

    const { getRequest, postRequest, putRequest, deleteRequest } = useRequests();

    const [AlertData, setAlertData] = React.useState({
        variant: '',
        message: '',
    });

    const resetAlertData = () => {
        setTimeout(() => {
            setAlertData((AlertData) => ({
                ...AlertData,
                variant: '',
                message: '',
            }));
        }, process.env.REACT_APP_ALERT_AUTO_CLOSE);
    };

    const [formData, setFormData] = useState({
        category_name: "",
        category_code: "",
        seats: "",
        status: "Enable",
    });

    const fetchVenueDetails = async () => {
        const response = await getRequest(`venue-details/${v_id}`);
        if (response && response.code === 200) {
            setVenue(response.data);
        }
    };

    const fetchSeatCategories = async () => {
        setLoading(true);
        const response = await getRequest(`venue-seat-categories/${v_id}`);
        if (response && response.code === 200) {
            setSeatCategories(response.data);
        }
        setLoading(false);
    };

    useEffect(() => {
        fetchVenueDetails();
        fetchSeatCategories();
    }, [v_id]);

    // Function to count and validate seats
    const countSeats = (seatString) => {
        if (!seatString || seatString.trim() === '') return 0;
        return seatString.split(',').filter(seat => seat.trim() !== '').length;
    };

    // Function to validate seat format
    const validateSeats = (seatString) => {
        if (!seatString || seatString.trim() === '') return true;
        const seats = seatString.split(',');
        const seatPattern = /^[A-Z]+\d+$/; // Pattern like A1, B2, VIP1, etc.
        
        for (let seat of seats) {
            const trimmedSeat = seat.trim();
            if (trimmedSeat !== '' && !seatPattern.test(trimmedSeat)) {
                return false;
            }
        }
        return true;
    };

    // Function to format seats (remove duplicates and empty values)
    const formatSeats = (seatString) => {
        if (!seatString) return '';
        const seats = seatString.split(',')
            .map(seat => seat.trim().toUpperCase())
            .filter(seat => seat !== '');
        
        // Remove duplicates
        const uniqueSeats = [...new Set(seats)];
        return uniqueSeats.join(',');
    };

    // Function to calculate total seats from all categories except current one being edited
    const getTotalSeatsExcludingCurrent = () => {
        if (modalType === "edit" && selectedCategory) {
            // Exclude current category being edited
            return seatCategories
                .filter(cat => cat.sc_id !== selectedCategory.sc_id)
                .reduce((total, cat) => total + parseInt(cat.total_seats || 0), 0);
        } else {
            // For add mode, include all existing categories
            return seatCategories.reduce((total, cat) => total + parseInt(cat.total_seats || 0), 0);
        }
    };

    // Function to get remaining capacity
    const getRemainingCapacity = () => {
        const usedSeats = getTotalSeatsExcludingCurrent();
        return parseInt(venue.capacity || 0) - usedSeats;
    };

    // Enhanced validation schema with capacity check
    const validation = useFormik({
        enableReinitialize: true,
        initialValues: formData,
        validationSchema: Yup.object({
            category_name: Yup.string().required('Category Name is required'),
            category_code: Yup.string()
                .required('Category Code is required')
                .matches(/^[A-Z]+$/, 'Category Code must contain only uppercase letters')
                .test('unique-code', 'Category code already exists', function(value) {
                    if (!value) return true;
                    
                    // Check if code exists in other categories
                    const existingCategory = seatCategories.find(cat => 
                        cat.category_code === value && 
                        (modalType === "add" || cat.sc_id !== selectedCategory?.sc_id)
                    );
                    return !existingCategory;
                }),
            seats: Yup.string()
                .required('Seats are required')
                .test('valid-format', 'Invalid seat format. Use format like A1,B2,C3', function(value) {
                    return validateSeats(value);
                })
                .test('min-seats', 'At least one seat is required', function(value) {
                    return countSeats(value) >= 1;
                })
                .test('max-seats', 'Cannot exceed 500000 seats', function(value) {
                    return countSeats(value) <= 500000;
                })
                .test('venue-capacity', function(value) {
                    const currentSeats = countSeats(value);
                    const remainingCapacity = getRemainingCapacity();
                    const venueCapacity = parseInt(venue.capacity || 0);
                    
                    if (currentSeats > remainingCapacity) {
                        return this.createError({
                            message: `Exceeds venue capacity! Available: ${remainingCapacity} seats (Venue capacity: ${venueCapacity})`
                        });
                    }
                    return true;
                })
                .test('duplicate-seats', 'Some seats already exist in other categories', function(value) {
                    if (!value) return true;
                    
                    const currentSeats = formatSeats(value).split(',').filter(seat => seat.trim() !== '');
                    const existingSeats = [];
                    
                    // Collect all existing seats from other categories
                    seatCategories.forEach(cat => {
                        if (modalType === "add" || cat.sc_id !== selectedCategory?.sc_id) {
                            if (cat.seats) {
                                const catSeats = cat.seats.split(',').map(seat => seat.trim()).filter(seat => seat !== '');
                                existingSeats.push(...catSeats);
                            }
                        }
                    });
                    
                    // Check for duplicates
                    const duplicates = currentSeats.filter(seat => existingSeats.includes(seat));
                    
                    if (duplicates.length > 0) {
                        return this.createError({
                            message: `Duplicate seats found: ${duplicates.slice(0, 5).join(', ')}${duplicates.length > 5 ? '...' : ''}`
                        });
                    }
                    return true;
                }),
            status: Yup.string().required('Status is required'),
        }),
        onSubmit: async (values) => {
            setSubmitProcessing(true);
            
            try {
                // Final capacity check before submission
                const formattedSeats = formatSeats(values.seats);
                const totalSeats = countSeats(formattedSeats);
                const remainingCapacity = getRemainingCapacity();
                
                if (totalSeats > remainingCapacity) {
                    setAlertData({
                        variant: 'error',
                        message: `Cannot add ${totalSeats} seats. Only ${remainingCapacity} seats available in venue capacity.`,
                    });
                    resetAlertData();
                    return;
                }
                
                const submitData = {
                    ...values,
                    v_id: v_id,
                    seats: formattedSeats,
                    total_seats: totalSeats
                };

                let response;
                if (modalType === "add") {
                    response = await postRequest("seat-category-add", JSON.stringify(submitData));
                } else {
                    response = await putRequest(`seat-category-edit/${selectedCategory.sc_id}`, JSON.stringify(submitData));
                }

                if (response && response.code === 200) {
                    setAlertData({
                        variant: 'success',
                        message: `Seat category ${modalType === "add" ? "added" : "updated"} successfully!`,
                    });
                    resetAlertData();
                    fetchSeatCategories();
                    handleCloseModal();
                } else {
                    setAlertData({
                        variant: 'error',
                        message: response.message || `Failed to ${modalType} seat category.`,
                    });
                    resetAlertData();
                }
            } catch (error) {
                console.error("Error: ", error);
                setAlertData({
                    variant: 'error',
                    message: `An error occurred while ${modalType === "add" ? "adding" : "updating"} the seat category.`,
                });
                resetAlertData();
            } finally {
                setSubmitProcessing(false);
            }
        }
    });

    const handleShowModal = (type, category = null) => {
        setModalType(type);
        setSelectedCategory(category);
        
        if (type === "edit" && category) {
            // For edit mode - populate with category data
            setFormData({
                category_name: category.category_name || "",
                category_code: category.category_code || "",
                seats: category.seats || "",
                status: category.status || "Enable",
            });
        } else {
            // For add mode - reset to default values
            setFormData({
                category_name: "",
                category_code: "",
                seats: "",
                status: "Enable",
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setModalType("add");
        setSelectedCategory(null);
        setSubmitProcessing(false);
        
        // Reset form data to default values
        setFormData({
            category_name: "",
            category_code: "",
            seats: "",
            status: "Enable",
        });
        
        // Reset formik validation state
        validation.resetForm();
    };

    // Status change handlers
    const handleOpenStatusModal = (category) => {
        setSelectedCategory(category);
        setShowStatusModal(true);
    };

    const handleCloseStatusModal = () => {
        setShowStatusModal(false);
        setSelectedCategory(null);
        setStatusProcessing(false);
    };

    const handleStatusChange = async () => {
        if (selectedCategory) {
            const newStatus = selectedCategory.status === "Enable" ? "Disable" : "Enable";
            setStatusProcessing(true);

            try {
                const jsonpar = JSON.stringify({ status: newStatus });
                const response = await putRequest(`seat-category-status/${selectedCategory.sc_id}`, jsonpar);

                if (response && response.code === 200) {
                    const updatedCategories = seatCategories.map((category) =>
                        category.sc_id === selectedCategory.sc_id ? { ...category, status: newStatus } : category
                    );
                    setSeatCategories(updatedCategories);
                    setAlertData({
                        variant: 'success',
                        message: `Seat category ${newStatus.toLowerCase()}d successfully`,
                    });
                    resetAlertData();
                } else {
                    setAlertData({
                        variant: 'error',
                        message: response?.message || `Failed to update seat category status.`,
                    });
                    resetAlertData();
                }
            } catch (error) {
                console.error("Error updating status:", error);
                setAlertData({
                    variant: 'error',
                    message: 'Error updating seat category status',
                });
                resetAlertData();
            } finally {
                setStatusProcessing(false);
                handleCloseStatusModal();
            }
        }
    };

    const handleDeleteCategory = async () => {
        if (selectedCategory) {
            setDeleteProcessing(true);
            
            try {
                const response = await deleteRequest(`seat-category-delete/${selectedCategory.sc_id}`);
                if (response && response.code === 200) {
                    const updatedCategories = seatCategories.filter((category) => category.sc_id !== selectedCategory.sc_id);
                    setSeatCategories(updatedCategories);
                    setAlertData({
                        variant: 'success',
                        message: 'Seat category deleted successfully!',
                    });
                    resetAlertData();
                } else {
                    setAlertData({
                        variant: 'error',
                        message: 'Failed to delete seat category',
                    });
                    resetAlertData();
                }
            } catch (error) {
                console.error("Error deleting category:", error);
                setAlertData({
                    variant: 'error',
                    message: 'Error deleting seat category',
                });
                resetAlertData();
            } finally {
                setDeleteProcessing(false);
                setShowDeleteModal(false);
                setSelectedCategory(null);
            }
        }
    };

    const columns = [
        {
            name: "Category Name",
            selector: (row) => row.category_name,
            sortable: true,
        },
        {
            name: "Category Code",
            selector: (row) => row.category_code,
            sortable: true,
            width: "150px"
        },
        {
            name: "Total Seats",
            selector: (row) => row.total_seats,
            sortable: true,
            width: "120px"
        },
        {
            name: "Seats",
            cell: (row) => (
                <div style={{ maxWidth: "200px", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {row.seats && row.seats.length > 50 ? `${row.seats.substring(0, 50)}...` : row.seats}
                </div>
            ),
            width: "250px"
        },
        {
            name: "Status",
            cell: (row) => (
                <button
                    onClick={() => handleOpenStatusModal(row)}
                    className={`btn ${
                        row.status === "Enable" ? "btn-success" : "btn-danger"
                    } btn-xxs`}
                    disabled={statusProcessing || deleteProcessing || submitProcessing}
                >
                    {row.status === "Enable" ? "Enable" : "Disable"}
                </button>
            ),
            center: true,
            width: "120px",
        },
        {
            name: "Actions",
            cell: (row) => (
                <div className="text-center">
                    <button 
                        className={`btn btn-primary btn-xxs me-2 ${(statusProcessing || deleteProcessing || submitProcessing) ? 'disabled' : ''}`}
                        onClick={() => handleShowModal("edit", row)}
                        disabled={statusProcessing || deleteProcessing || submitProcessing}
                        style={{ 
                            opacity: (statusProcessing || deleteProcessing || submitProcessing) ? 0.6 : 1 
                        }}
                    >
                        <i className="fa-regular fa-pen-to-square"></i>
                    </button>
                    <button 
                        className="btn btn-danger btn-xxs" 
                        onClick={() => {
                            setSelectedCategory(row);
                            setShowDeleteModal(true);
                        }}
                        disabled={statusProcessing || deleteProcessing || submitProcessing}
                        style={{ 
                            opacity: (statusProcessing || deleteProcessing || submitProcessing) ? 0.6 : 1 
                        }}
                    >
                        <i className="fa-solid fa-trash"></i>
                    </button>
                </div>
            ),
            center: true,
            width: "150px",
        },
    ];

    const customStyles = {
        header: {
            style: {
                whiteSpace: "normal",
            },
        },
        headCells: {
            style: {
                color: "#341439",
                fontSize: "17px",
                fontWeight: "600",
                textTransform: "capitalize",
                paddingLeft: "8px",
                paddingRight: "8px",
            },
        },
        cells: {
            style: {
                fontSize: "16px",
                padding: "12px 9px",
                color: "#7E7E7E",
            },
        },
        pagination: {
            style: {
                fontSize: "16px",
            },
        },
    };

    return (
        <Fragment>
            <div className="d-sm-flex d-block justify-content-between align-items-center mb-4">
                <h2 className="text-black font-w600 mb-0">
                    <i className="flaticon-381-controls me-2"></i>
                    Seat Categories - {venue.name}
                </h2>
                {AlertData.message !== "" && (
                    <Snackbars variant={AlertData.variant} message={AlertData.message} />
                )}
                <div>
                    <button
                        onClick={() => handleShowModal("add")}
                        className="btn btn-rounded btn-primary me-2"
                        disabled={statusProcessing || deleteProcessing || submitProcessing}
                        style={{ 
                            opacity: (statusProcessing || deleteProcessing || submitProcessing) ? 0.6 : 1 
                        }}
                    >
                        <i className="fa-solid fa-plus me-2"></i>
                        Add Category
                    </button>
                    <Link 
                        to="/venue-list" 
                        className="btn btn-rounded btn-primary"
                        style={{ 
                            pointerEvents: (statusProcessing || deleteProcessing || submitProcessing) ? 'none' : 'auto',
                            opacity: (statusProcessing || deleteProcessing || submitProcessing) ? 0.6 : 1 
                        }}
                    >
                        <i className="fa-solid fa-arrow-left me-2"></i>
                        Back to List
                    </Link>
                </div>
            </div>

            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Header>
                            <h4 className="card-title">Venue Information</h4>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <p><strong>Venue Name:</strong> {venue.name}</p>
                                    <p><strong>City:</strong> {venue.city}</p>
                                    <p><strong>Address:</strong> {venue.address}</p>
                                </Col>
                                <Col md={6}>
                                    <p><strong>Total Capacity:</strong> {venue.capacity}</p>
                                    <p><strong>Status:</strong> 
                                        <span className={`badge ms-2 ${venue.status === 'Enable' ? 'badge-success' : 'badge-danger'}`}>
                                            {venue.status}
                                        </span>
                                    </p>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Row>
                <Col lg={12}>
                    <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h4 className="card-title">Seat Categories</h4>
                            <span className="badge badge-primary">
                                Total Categories: {seatCategories.length}
                            </span>
                        </Card.Header>
                        <Card.Body>
                            {loading ? (
                                <div className="text-center">
                                    <div className="spinner-border" role="status">
                                        <span className="sr-only">Loading...</span>
                                    </div>
                                </div>
                            ) : (
                                <DataTable
                                    columns={columns}
                                    data={seatCategories}
                                    customStyles={customStyles}
                                    pagination
                                    highlightOnHover
                                    responsive
                                    progressPending={statusProcessing || deleteProcessing || submitProcessing}
                                    progressComponent={
                                        <div className="text-center p-4">
                                            <Spinner animation="border" variant="primary" />
                                            <p className="mt-2">
                                                {statusProcessing ? "Updating status..." : 
                                                 deleteProcessing ? "Deleting category..." :
                                                 submitProcessing ? "Processing..." : "Processing..."}
                                            </p>
                                        </div>
                                    }
                                />
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalType === "add" ? "Add Seat Category" : "Edit Seat Category"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <form onSubmit={(e) => e.preventDefault()}>
                        {/* Capacity Info Alert */}
                        <div className="alert alert-info mb-3">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>Venue Capacity:</strong> {venue.capacity} seats
                                </div>
                                <div>
                                    <strong>Available:</strong> {getRemainingCapacity()} seats
                                </div>
                            </div>
                            <small className="text-muted">
                                Total allocated: {getTotalSeatsExcludingCurrent()} seats
                            </small>
                        </div>

                        <Row>
                            <Col md={6}>
                                <div className="form-group mb-3">
                                    <label>Category Name <span className="text-danger">*</span>:</label>
                                    <input
                                        type="text"
                                        name="category_name"
                                        className={`form-control ${validation.touched.category_name && validation.errors.category_name ? 'is-invalid' : ''}`}
                                        placeholder="e.g., VIP, Premium, General"
                                        value={validation.values.category_name}
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        disabled={submitProcessing}
                                    />
                                    {validation.errors.category_name && validation.touched.category_name && (
                                        <small className="text-danger">{validation.errors.category_name}</small>
                                    )}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-group mb-3">
                                    <label>Category Code <span className="text-danger">*</span>:</label>
                                    <input
                                        type="text"
                                        name="category_code"
                                        className={`form-control ${validation.touched.category_code && validation.errors.category_code ? 'is-invalid' : ''}`}
                                        placeholder="e.g., A, B, VIP"
                                        value={validation.values.category_code}
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        style={{ textTransform: 'uppercase' }}
                                        disabled={submitProcessing}
                                    />
                                    {validation.errors.category_code && validation.touched.category_code && (
                                        <><small className="text-danger">{validation.errors.category_code}</small><br/></>
                                    )}
                                    <small className="form-text text-muted">Only uppercase letters allowed</small>
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-group mb-3">
                                    <label>Status <span className="text-danger">*</span>:</label>
                                    <select
                                        name="status"
                                        className={`form-control ${validation.touched.status && validation.errors.status ? 'is-invalid' : ''}`}
                                        value={validation.values.status}
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        disabled={submitProcessing}
                                    >
                                        <option value="Enable">Enable</option>
                                        <option value="Disable">Disable</option>
                                    </select>
                                    {validation.errors.status && validation.touched.status && (
                                        <small className="text-danger">{validation.errors.status}</small>
                                    )}
                                </div>
                            </Col>
                            <Col md={6}>
                                <div className="form-group mb-3">
                                    <label>Total Seats:</label>
                                    <input
                                        type="text"
                                        className={`form-control ${countSeats(validation.values.seats) > getRemainingCapacity() ? 'is-invalid' : ''}`}
                                        value={countSeats(validation.values.seats)}
                                        disabled
                                        style={{ 
                                            backgroundColor: countSeats(validation.values.seats) > getRemainingCapacity() ? '#ffe6e6' : '#f8f9fa',
                                            color: countSeats(validation.values.seats) > getRemainingCapacity() ? '#dc3545' : 'inherit'
                                        }}
                                    />
                                    <small className="form-text text-muted">
                                        Auto-calculated from seats | Max: {getRemainingCapacity()}
                                    </small>
                                </div>
                            </Col>
                            <Col md={12}>
                                <div className="form-group mb-3">
                                    <label>Seats <span className="text-danger">*</span>:</label>
                                    <textarea
                                        rows={4}
                                        name="seats"
                                        className={`form-control ${validation.touched.seats && validation.errors.seats ? 'is-invalid' : ''}`}
                                        placeholder="Enter seats separated by commas (e.g., A1,A2,B1,B2,VIP1,VIP2)"
                                        value={validation.values.seats}
                                        onChange={validation.handleChange}
                                        onBlur={validation.handleBlur}
                                        style={{ textTransform: 'uppercase' }}
                                        disabled={submitProcessing}
                                    />
                                    {validation.errors.seats && validation.touched.seats && (
                                        <><small className="text-danger">{validation.errors.seats}</small><br/></>
                                    )}
                                    <small className="form-text text-muted">
                                        Format: Letter(s) followed by number(s). Example: A1,B2,VIP1,GOLD10
                                    </small>
                                </div>
                            </Col>
                        </Row>
                        {validation.values.seats && countSeats(validation.values.seats) > 0 && (
                            <div className={`alert ${countSeats(validation.values.seats) > getRemainingCapacity() ? 'alert-danger' : 'alert-info'}`}>
                                <strong>Preview:</strong>
                                <br />
                                <strong>Total Seats:</strong> {countSeats(validation.values.seats)}
                                <br />
                                <strong>Remaining Capacity After:</strong> {getRemainingCapacity() - countSeats(validation.values.seats)}
                                {countSeats(validation.values.seats) > getRemainingCapacity() && (
                                    <>
                                        <br />
                                        <strong className="text-danger">⚠️ Exceeds venue capacity!</strong>
                                    </>
                                )}
                            </div>
                        )}
                    </form>
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="light" 
                        onClick={handleCloseModal}
                        disabled={submitProcessing}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="primary" 
                        onClick={() => validation.handleSubmit()}
                        disabled={submitProcessing || countSeats(validation.values.seats) > getRemainingCapacity()}
                    >
                        {submitProcessing ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Processing...
                            </>
                        ) : (
                            modalType === "add" ? "Add Category" : "Update Category"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Status Change Modal */}
            <Modal show={showStatusModal} onHide={handleCloseStatusModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {selectedCategory?.status === "Enable" ? "Disable" : "Enable"} Seat Category
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to {selectedCategory?.status === "Enable" ? "disable" : "enable"} the seat category{" "}
                    <strong>{selectedCategory?.category_name}</strong>?
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="light" 
                        onClick={handleCloseStatusModal}
                        disabled={statusProcessing}
                    >
                        Cancel
                    </Button>
                    <Button
                        className={`btn btn-${
                            selectedCategory?.status === "Enable" ? "danger" : "success"
                        }`}
                        onClick={handleStatusChange}
                        disabled={statusProcessing}
                    >
                        {statusProcessing ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Processing...
                            </>
                        ) : (
                            selectedCategory?.status === "Enable" ? "Disable" : "Enable"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Delete Seat Category</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to delete the seat category{" "}
                    <strong>{selectedCategory?.category_name}</strong>? This action cannot be undone.
                </Modal.Body>
                <Modal.Footer>
                    <Button 
                        variant="light" 
                        onClick={() => setShowDeleteModal(false)}
                        disabled={deleteProcessing}
                    >
                        Cancel
                    </Button>
                    <Button 
                        variant="danger" 
                        onClick={handleDeleteCategory}
                        disabled={deleteProcessing}
                    >
                        {deleteProcessing ? (
                            <>
                                <Spinner
                                    as="span"
                                    animation="border"
                                    size="sm"
                                    role="status"
                                    aria-hidden="true"
                                    className="me-2"
                                />
                                Processing...
                            </>
                        ) : (
                            "Delete"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default SeatCategories;